import { useState, useCallback } from 'react';
import axiosInstance from '../../../axiosInstance';
import { updateDownloadProgress } from '../../../components/common/DownloadProgress';

/**
 * Custom hook for downloading files
 *
 * SUPPORTED FILE TYPES FOR DOWNLOAD:
 * - Documents: PDF, Word (.doc, .docx), PowerPoint (.ppt, .pptx), Excel (.xls, .xlsx), Text, CSV
 * - Images: JPEG, PNG, GIF
 * - Compressed: ZIP, RAR, 7Z, TAR, GZIP, BZIP2, XZ
 * - All file types uploaded to the system are downloadable
 *
 * DOWNLOAD BEHAVIOR:
 * - Files are downloaded with 'Content-Disposition: attachment' header
 * - File metadata is included in 'X-File-Info' response header
 * - Progress tracking is available for large files
 * - Original filename is preserved when possible
 *
 * @returns {object} - { downloadFile, downloading, error, getFileInfo }
 */
export const useFileDownload = () => {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Downloads or previews a file by ID.
   *
   * @param {Object} file - The file object { id, name, mime_type }
   * @param {Object} options - Download options
   * @param {boolean} options.forceDownload - Force download instead of preview
   * @returns {Promise<void>}
   */
  /**
   * Downloads or previews a file by ID.
   * All files now use the centralized file module (files.id = sub_form_files.id after migration).
   *
   * @param {Object} file - The file object { id, name, mime_type }
   * @param {Object} options - Download options
   * @param {boolean} options.forceDownload - Force download instead of preview
   * @returns {Promise<void>}
   */
  const downloadFileForEndUserFilledForm = useCallback(
    async (file, options = {}) => {
      if (downloading || !file?.id) return;

      const { forceDownload = false, includeDeleted = false } = options;
      const isPreviewable =
        file.mime_type?.startsWith('image/') ||
        file.mime_type === 'application/pdf';

      setDownloading(true);

      try {
        // All files use the centralized file module endpoint
        const action = forceDownload || !isPreviewable ? 'download' : 'preview';
        const params = includeDeleted ? '?includeDeleted=true' : '';
        const endpoint = `/api/v1/files/${file.id}/${action}${params}`;

        const response = await axiosInstance.get(endpoint, {
          responseType: 'blob',
        });

        const blob = new Blob([response.data], {
          type: file.mime_type || 'application/octet-stream',
        });
        const blobUrl = window.URL.createObjectURL(blob);

        if (isPreviewable && !forceDownload) {
          window.open(blobUrl, '_blank', 'noopener,noreferrer');
        } else {
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = file.name;
          link.click();
        }

        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
      } finally {
        setDownloading(false);
      }
    },
    [downloading]
  );

  /**
   * Extract and parse file information from response headers
   * @param {object} headers - Response headers object
   * @returns {object|null} - Parsed file info or null if not available
   */
  const parseFileInfo = (headers) => {
    const fileInfoHeader = headers['x-file-info'];
    if (fileInfoHeader) {
      try {
        return JSON.parse(fileInfoHeader);
      } catch (e) {
        console.warn('Could not parse file info from headers:', e);
        return null;
      }
    }
    return null;
  };

  /**
   * Get file information without downloading (uses dedicated file info endpoint)
   * @param {number|string} fileId - The file ID
   * @returns {Promise<object|null>} - File info object or null if not found
   */
  const getFileInfo = async (fileId) => {
    if (!fileId) {
      return null;
    }

    try {
      const endpoint = `/api/files/${fileId}`;

      const response = await axiosInstance.get(endpoint);

      if (response.data.success && response.data.data) {
        return response.data.data;
      }

      return null;
    } catch (err) {
      console.error('Error getting file info:', err);
      return null;
    }
  };

  const downloadFile = async (fileId, fileName = null) => {
    if (!fileId) {
      console.error('File ID is required for download');
      return;
    }

    // Use fileId as the unique identifier for progress tracking
    const progressId = `download_${fileId}`;

    try {
      setDownloading(true);
      setError(null);

      // Start progress tracking
      updateDownloadProgress(progressId, {
        downloading: true,
        progress: 0,
        fileName: fileName,
      });

      // Determine endpoint based on persona
      const endpoint = `/api/v1/files/${fileId}/download`;

      const response = await axiosInstance.get(endpoint, {
        responseType: 'blob',
        onDownloadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          updateDownloadProgress(progressId, {
            downloading: true,
            progress: percentCompleted,
            fileName: fileName,
          });
        },
      });

      // Get file info from headers if available
      let finalFileName = fileName;
      const parsedFileInfo = parseFileInfo(response.headers);
      if (parsedFileInfo && !finalFileName) {
        finalFileName = parsedFileInfo.original_name || parsedFileInfo.name;
      }

      // Fallback filename
      if (!finalFileName) {
        finalFileName = `download_${fileId}`;
      }

      // Create download link
      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'application/octet-stream',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = finalFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Clear progress on successful download
      updateDownloadProgress(progressId, {
        downloading: false,
        progress: 100,
      });

      return true;
    } catch (err) {
      console.error('Error downloading file:', err);
      const errorMessage =
        err.response?.data?.message || 'Failed to download file';
      setError(errorMessage);

      // Clear progress on error
      updateDownloadProgress(progressId, {
        downloading: false,
        progress: 0,
      });

      return false;
    } finally {
      setDownloading(false);
      setError(null);
    }
  };

  return {
    downloadFile,
    downloading,
    error,
    getFileInfo,
    downloadFileForEndUserFilledForm,
  };
};

export default useFileDownload;
